import {
  CLAUDE_BUILT_IN_MODELS,
  CODEX_BUILT_IN_MODELS,
  DEFAULT_APEX_PROFILES,
  DEFAULT_MODEL_BY_PROVIDER,
  EMPTY_MODEL_CAPABILITIES,
  type ModelSelection,
  type ProviderKind,
  type ServerProviderModel,
} from "@cmmd-forge/contracts";
import { resolveSelectableModel } from "@cmmd-forge/shared/model";

import {
  resolveProvisioningProviderEligibility,
  type ForgeProviderAccessState,
  type ProviderEligibility,
} from "../../cmmd/forgeProviderAccess";
export interface ProvisioningProviderCatalogEntry {
  readonly provider: Extract<ProviderKind, "codex" | "claudeAgent" | "cmmd">;
  readonly models: ReadonlyArray<ServerProviderModel>;
  readonly eligibility: ProviderEligibility;
}

const CMMD_PROVISIONING_MODELS: ReadonlyArray<ServerProviderModel> = DEFAULT_APEX_PROFILES.map(
  (profile) => ({
    slug: profile.slug,
    name: profile.displayName ?? profile.slug,
    isCustom: false,
    capabilities: EMPTY_MODEL_CAPABILITIES,
  }),
);

type ProvisioningProviderCatalogDefinition = Omit<ProvisioningProviderCatalogEntry, "eligibility">;

const PROVISIONING_PROVIDER_CATALOG: ReadonlyArray<ProvisioningProviderCatalogDefinition> = [
  { provider: "codex", models: CODEX_BUILT_IN_MODELS },
  { provider: "claudeAgent", models: CLAUDE_BUILT_IN_MODELS },
  { provider: "cmmd", models: CMMD_PROVISIONING_MODELS },
];

/**
 * The provider and model catalog Forge plans to provision for Cloud execution,
 * paired with CMMD's current policy and credential authority.
 *
 * This is a plan, not a runtime inventory snapshot. Callers must revalidate an
 * exact selection against the new runtime before starting a turn.
 */
export function resolveProvisioningProviderCatalog(
  access: ForgeProviderAccessState,
): ReadonlyArray<ProvisioningProviderCatalogEntry> | null {
  if (access.status !== "ready" || access.policy === null) return null;
  return PROVISIONING_PROVIDER_CATALOG.flatMap((entry) => {
    const eligibility = resolveProvisioningProviderEligibility({
      providerKind: entry.provider,
      access,
    });
    if (!eligibility.eligible && eligibility.reason === "not-offered") return [];
    return [{ ...entry, eligibility }];
  });
}

export function resolveProvisioningModelSelection(input: {
  readonly catalog: ReadonlyArray<ProvisioningProviderCatalogEntry>;
  readonly preferredProvider: ProviderKind | null;
  readonly preferredModel: string | null;
}): ModelSelection | null {
  const selectableCatalog = input.catalog.filter((entry) => entry.eligibility.eligible);
  const preferredEntry = input.preferredProvider
    ? selectableCatalog.find((entry) => entry.provider === input.preferredProvider)
    : undefined;
  const entry = preferredEntry ?? selectableCatalog[0];
  if (!entry) return null;

  const preferredModel =
    preferredEntry && input.preferredModel
      ? resolveSelectableModel(entry.provider, input.preferredModel, entry.models)
      : null;
  const defaultModel = resolveSelectableModel(
    entry.provider,
    DEFAULT_MODEL_BY_PROVIDER[entry.provider],
    entry.models,
  );
  const model = preferredModel ?? defaultModel ?? entry.models[0]?.slug;
  return model ? { provider: entry.provider, model } : null;
}
